import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useStore } from '@/store/useStore';
import { APIServiceType } from '@/api/types';
import { fetchRecentlyPlayedApi } from './fetch-recently-played';
import handleRecentlyPlayedData from './helpers/handle-recently-played-data';

const RECENTLY_PLAYED_QUERY_KEY = ['recently-played'] as const;

/**
 * Loads recently played with persisted hydration + background revalidation.
 */
export const useRecentlyPlayed = () => {
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const sessionResetRevision = useStore((s) => s.sessionResetRevision);

  const query = useQuery({
    queryKey: RECENTLY_PLAYED_QUERY_KEY,
    queryFn: fetchRecentlyPlayedApi,
    meta: { apiService: APIServiceType.RECENTLY_PLAYED },
    enabled: true,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { data, refetch } = query;

  // Recently-played ordering changes every time the user plays anything — persisted hydrate
  // diffing from the latest server response is the normal case, not a regression. We keep
  // the background refetch so the list stays current but skip the soft-reload: tearing
  // down the game client mid-launch because the recently-played list moved is never the
  // right answer.
  useEffect(() => {
    refetch();
  }, [gameResetRevision, sessionResetRevision, refetch]);

  useEffect(() => {
    if (!data) {
      return;
    }
    handleRecentlyPlayedData(data);
  }, [data]);

  return query;
};
